import { useState, useEffect } from 'react';
import {
  Truck, AlertTriangle, Timer, Navigation, Loader2, CheckCircle, Send
} from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { db } from '../../utils/firebase/client';
import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { getCompanyId } from '../../utils/tenantContext';
import { toast } from 'sonner';
import { useTheme } from '../../hooks/useTheme';

interface Hauler {
  id: string;
  name: string;
  status: 'en_route' | 'loading' | 'idle' | 'offline';
  lat?: number;
  lng?: number;
  eta?: string;
  cargo?: string;
  capacity?: number;
  load?: number;
  destination?: string;
  driver?: string;
}

interface Tank {
  id: string;
  wellId: string;
  wellName: string;
  location: string;
  level: number;
  type: 'oil' | 'water';
  capacity: number;
  status: 'critical' | 'warning' | 'normal';
  timeToFull?: string;
}

interface Vendor {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  serviceTypes?: string[];
}

interface LogisticsCommandProps {
  companyId?: string;  // Passed from parent for reliable tenant context
}

export default function LogisticsCommand({ companyId: propCompanyId }: LogisticsCommandProps) {
  const { isDarkMode } = useTheme();
  const [selectedHauler, setSelectedHauler] = useState<string | null>(null);
  const [autoDispatch, setAutoDispatch] = useState(true);
  const [loading, setLoading] = useState(true);
  const [haulers, setHaulers] = useState<Hauler[]>([]);
  const [criticalTanks, setCriticalTanks] = useState<Tank[]>([]);

  // Dispatch Dialog State
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [selectedTank, setSelectedTank] = useState<Tank | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [isDispatching, setIsDispatching] = useState(false);

  // Use prop companyId first (reliable from parent user object), fallback to context
  const companyId = propCompanyId || getCompanyId();

  // Fetch real data from Firestore
  useEffect(() => {
    if (!companyId || companyId === 'default') {
      console.warn('⚠️ LogisticsCommand: No valid companyId available');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        // 1. Get haulers/trucks from users or fleet collection
        const haulersData: Hauler[] = [];

        // Try fleet collection first
        try {
          const fleetRef = collection(db, `companies/${companyId}/fleet`);
          const fleetSnapshot = await getDocs(fleetRef);

          fleetSnapshot.forEach(doc => {
            const data = doc.data();
            haulersData.push({
              id: doc.id,
              name: data.name || data.truckNumber || `Unit ${doc.id.slice(0, 4)}`,
              status: data.status || 'idle',
              lat: data.location?.lat,
              lng: data.location?.lng,
              eta: data.eta || '-',
              cargo: data.cargoType || 'Unknown',
              capacity: data.capacity || 0,
              load: data.currentLoad || 0,
              destination: data.destination || '-',
              driver: data.driverName || 'Unassigned'
            });
          });
        } catch (err) {
        }

        // If no fleet, check for users who are drivers/haulers
        if (haulersData.length === 0) {
          try {
            const usersRef = collection(db, `companies/${companyId}/users`);
            const usersSnapshot = await getDocs(usersRef);

            usersSnapshot.forEach(doc => {
              const data = doc.data();
              if (data.role === 'driver' || data.role === 'hauler' || data.vehicleAssigned) {
                haulersData.push({
                  id: doc.id,
                  name: data.vehicleName || `Vehicle - ${data.name || 'Unknown'}`,
                  status: data.status || 'offline',
                  lat: data.lastLocation?.lat,
                  lng: data.lastLocation?.lng,
                  driver: data.name || 'Unknown',
                  cargo: 'Not specified',
                  destination: '-'
                });
              }
            });
          } catch (err) {
          }
        }

        setHaulers(haulersData);

        // 2. Get vendors for dispatch
        try {
          const vendorsRef = collection(db, `companies/${companyId}/vendors`);
          const vendorsSnapshot = await getDocs(vendorsRef);
          const vendorsData: Vendor[] = [];

          vendorsSnapshot.forEach(doc => {
            const data = doc.data();
            if (data.status === 'approved' || data.status === 'active') {
              vendorsData.push({
                id: doc.id,
                name: data.companyName || data.name || 'Unknown Vendor',
                email: data.email,
                phone: data.phone,
                serviceTypes: data.serviceTypes || []
              });
            }
          });

          setVendors(vendorsData);
        } catch (err) {
        }

        // 3. Get tank levels from wells - look for high fill levels
        const tanksData: Tank[] = [];

        try {
          const wellsRef = collection(db, `companies/${companyId}/wells`);
          const wellsSnapshot = await getDocs(wellsRef);

          for (const wellDoc of wellsSnapshot.docs) {
            const wellData = wellDoc.data();

            // Check tank levels from well data or latest production entry
            const tankLevels = wellData.tankLevels || wellData.currentTankLevels;

            if (tankLevels) {
              // Process oil tanks
              if (tankLevels.oil && tankLevels.oil > 75) {
                tanksData.push({
                  id: `${wellDoc.id}-oil`,
                  wellId: wellDoc.id,
                  wellName: wellData.name || wellDoc.id,
                  location: wellData.lease || wellData.location?.name || 'Unknown Location',
                  level: tankLevels.oil,
                  type: 'oil',
                  capacity: wellData.oilTankCapacity || 400,
                  status: tankLevels.oil >= 90 ? 'critical' : 'warning'
                });
              }

              // Process water tanks
              if (tankLevels.water && tankLevels.water > 75) {
                tanksData.push({
                  id: `${wellDoc.id}-water`,
                  wellId: wellDoc.id,
                  wellName: wellData.name || wellDoc.id,
                  location: wellData.lease || wellData.location?.name || 'Unknown Location',
                  level: tankLevels.water,
                  type: 'water',
                  capacity: wellData.waterTankCapacity || 400,
                  status: tankLevels.water >= 90 ? 'critical' : 'warning'
                });
              }
            }
          }
        } catch (err) {
        }

        // Sort by level (highest first)
        tanksData.sort((a, b) => b.level - a.level);
        setCriticalTanks(tanksData.slice(0, 5)); // Top 5 critical

      } catch (error) {
        console.error('Failed to load logistics data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Refresh every 2 minutes
    const interval = setInterval(loadData, 120000);
    return () => clearInterval(interval);
  }, [companyId]);

  const openDispatchDialog = (tank: Tank) => {
    setSelectedTank(tank);
    setSelectedVendor('');
    setDispatchNotes(`Haul ${tank.type} from ${tank.wellName}. Tank currently at ${tank.level}% capacity.`);
    setShowDispatchDialog(true);
  };

  const handleDispatch = async () => {
    if (!companyId || !selectedTank) {
      toast.error('Missing required information');
      return;
    }

    if (vendors.length > 0 && !selectedVendor) {
      toast.error('Please select a vendor');
      return;
    }

    setIsDispatching(true);
    try {
      // Create a service ticket / dispatch order
      const ticketRef = collection(db, `companies/${companyId}/serviceTickets`);

      const ticketData = {
        type: 'hauling',
        priority: selectedTank.status === 'critical' ? 'high' : 'medium',
        status: 'open',
        wellId: selectedTank.wellId,
        wellName: selectedTank.wellName,
        description: dispatchNotes,
        fluidType: selectedTank.type,
        tankLevel: selectedTank.level,
        vendorId: selectedVendor || null,
        vendorName: vendors.find(v => v.id === selectedVendor)?.name || 'Unassigned',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        companyId
      };

      await addDoc(ticketRef, ticketData);

      toast.success(`Dispatch order created for ${selectedTank.wellName}`);
      setShowDispatchDialog(false);
      setSelectedTank(null);
      setSelectedVendor('');
      setDispatchNotes('');

    } catch (error) {
      console.error('Failed to create dispatch order:', error);
      toast.error('Failed to create dispatch order');
    } finally {
      setIsDispatching(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <Truck className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            Logistics Command
          </h2>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            Real-time fluid hauling optimization & dispatch
          </p>
        </div>
        <div className={`flex items-center gap-3 p-2 rounded-lg border ${isDarkMode
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
          }`}>
          <span className={`text-sm font-medium pl-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Auto-Dispatch AI
          </span>
          <button
            onClick={() => setAutoDispatch(!autoDispatch)}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              ${autoDispatch ? 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}
            `}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoDispatch ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* Dispatch Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={setShowDispatchDialog}>
        <DialogContent className={isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>
              Create Dispatch Order
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
              Create a hauling service ticket for {selectedTank?.wellName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Tank Info */}
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedTank?.wellName}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {selectedTank?.location}
                  </p>
                </div>
                <Badge variant={selectedTank?.status === 'critical' ? 'destructive' : 'secondary'}>
                  {selectedTank?.level}% Full
                </Badge>
              </div>
              <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedTank?.type.toUpperCase()} Tank • {selectedTank?.capacity || 400} BBL Capacity
              </div>
            </div>

            {/* Vendor Selection */}
            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Assign Vendor</Label>
              {vendors.length === 0 ? (
                <div className={`p-3 rounded-lg text-sm ${isDarkMode
                    ? 'bg-amber-900/20 text-amber-200'
                    : 'bg-amber-50 text-amber-800'
                  }`}>
                  No approved vendors found. The ticket will be created as unassigned.
                </div>
              ) : (
                <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                  <SelectTrigger className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
                    <SelectValue placeholder={`Select a ${selectedTank?.type} hauling vendor`} />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
                    {vendors
                      .filter(v => {
                        if (!v.serviceTypes || v.serviceTypes.length === 0) return true;
                        const type = selectedTank?.type || '';
                        const services = v.serviceTypes.map(s => s.toLowerCase());
                        return services.some(s => s.includes('haul') || s.includes('truck') || s.includes(type));
                      })
                      .map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Dispatch Notes</Label>
              <Textarea
                placeholder="Add any special instructions..."
                value={dispatchNotes}
                onChange={(e) => setDispatchNotes(e.target.value)}
                rows={3}
                className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDispatchDialog(false)}
              className={isDarkMode ? 'border-slate-700 hover:bg-slate-800' : ''}
            >
              Cancel
            </Button>
            <Button
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              onClick={handleDispatch}
              disabled={isDispatching}
            >
              {isDispatching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Create Dispatch Order
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="h-[500px] flex items-center justify-center">
          <Loader2 className={`h-12 w-12 animate-spin ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Critical Tank Levels - Redesigned with Gradients */}
          <div className="space-y-4">
            <h3 className={`text-sm font-bold uppercase tracking-wider flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              <AlertTriangle className="w-4 h-4" />
              Critical Levels ({criticalTanks.length})
            </h3>

            {criticalTanks.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`rounded-3xl overflow-hidden ${isDarkMode
                    ? 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-500'
                    : 'bg-gradient-to-br from-emerald-400/20 via-emerald-500/20 to-green-400/20 border-2 border-white/60 backdrop-blur'
                  }`}
              >
                <div className={`p-6 text-center ${isDarkMode ? '' : 'bg-white/85 backdrop-blur rounded-[1.3rem] m-1'
                  }`}>
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [1, 0.7, 1]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity
                    }}
                  >
                    <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-white' : 'text-emerald-600'
                      }`} />
                  </motion.div>
                  <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    All Clear
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-600'}`}>
                    No tanks at critical levels. Tank data will appear here when wells report high fill levels.
                  </p>
                </div>
              </motion.div>
            ) : (
              criticalTanks.map((tank, index) => (
                <motion.div
                  key={tank.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  className={`rounded-3xl overflow-hidden ${tank.status === 'critical'
                      ? isDarkMode
                        ? 'bg-gradient-to-br from-red-500 via-red-600 to-rose-500'
                        : 'bg-gradient-to-br from-red-400/20 via-red-500/20 to-rose-400/20 border-2 border-white/60 backdrop-blur'
                      : isDarkMode
                        ? 'bg-gradient-to-br from-orange-500 via-orange-600 to-amber-500'
                        : 'bg-gradient-to-br from-orange-400/20 via-orange-500/20 to-amber-400/20 border-2 border-white/60 backdrop-blur'
                    }`}
                >
                  <div className={`p-4 ${isDarkMode ? '' : 'bg-white/85 backdrop-blur rounded-[1.3rem] m-1'
                    }`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {tank.wellName}
                        </h4>
                        <p className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                          {tank.location} • {tank.type.toUpperCase()}
                        </p>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-bold ${isDarkMode
                          ? tank.status === 'critical'
                            ? 'bg-white/20 text-white'
                            : 'bg-white/20 text-white'
                          : tank.status === 'critical'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                        {tank.level}%
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className={`w-full rounded-full h-2 mb-3 ${isDarkMode ? 'bg-white/20' : 'bg-gray-200'
                      }`}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${tank.level}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className={`h-2 rounded-full ${isDarkMode
                            ? 'bg-white'
                            : tank.status === 'critical' ? 'bg-red-600' : 'bg-amber-600'
                          }`}
                      />
                    </div>

                    <div className="flex justify-between items-center">
                      <span className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-white/80' : 'text-gray-600'
                        }`}>
                        <Timer className="w-3 h-3" /> Needs attention
                      </span>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                          size="sm"
                          variant="outline"
                          className={`h-7 text-xs ${isDarkMode
                              ? 'border-white/20 text-white hover:bg-white/10'
                              : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          onClick={() => openDispatchDialog(tank)}
                        >
                          Dispatch Now
                        </Button>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Middle Column: Fleet Status - Keep existing visualization */}
          <div className="lg:col-span-2">
            <Card className={`h-[500px] relative overflow-hidden border ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-gray-200'
              }`}>
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'radial-gradient(#4f46e5 1px, transparent 1px)',
                backgroundSize: '24px 24px'
              }}></div>

              {/* Map UI Overlay */}
              <div className="absolute top-4 left-4 z-10">
                <div className={`px-3 py-1.5 rounded-md text-xs font-mono border ${isDarkMode
                    ? 'bg-slate-800/90 backdrop-blur text-white border-slate-700'
                    : 'bg-white/90 backdrop-blur text-gray-900 border-gray-300'
                  }`}>
                  ACTIVE HAULS • {haulers.length} EN ROUTE
                </div>
              </div>

              {/* Content */}
              <div className="relative w-full h-full flex flex-col items-center justify-center p-8">
                {haulers.length === 0 ? (
                  <div className={`text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Truck className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'opacity-40' : 'opacity-30'}`} />
                    <h3 className="text-xl font-bold mb-2">No Active Hauls</h3>
                    <p className={`max-w-md ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      There are no trucks currently en route. Dispatch a vendor to haul Oil or Water from your tanks.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Central Hub */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 bg-blue-500 rounded-full animate-ping absolute"></div>
                      <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white relative z-10"></div>
                    </div>

                    {/* Haulers */}
                    {haulers.map((hauler, i) => (
                      <motion.div
                        key={hauler.id}
                        initial={{ x: 0, y: 0 }}
                        animate={{
                          x: (i % 3 === 0 ? 100 : i % 3 === 1 ? -80 : 40),
                          y: (i % 3 === 0 ? -50 : i % 3 === 1 ? 60 : 120)
                        }}
                        transition={{ duration: 20, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
                        className="absolute top-1/2 left-1/2 cursor-pointer group"
                        onClick={() => setSelectedHauler(hauler.id)}
                      >
                        <div className={`
                          w-8 h-8 rounded-full flex items-center justify-center text-white shadow-lg transition-transform group-hover:scale-110
                          ${hauler.status === 'en_route' ? 'bg-emerald-500' : hauler.status === 'loading' ? 'bg-amber-500' : 'bg-gray-500'}
                        `}>
                          <Truck className="w-4 h-4" />
                        </div>
                        {/* Tooltip */}
                        <div className={`absolute -top-10 left-1/2 -translate-x-1/2 text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${isDarkMode ? 'bg-white text-gray-900' : 'bg-gray-900 text-white'
                          }`}>
                          {hauler.name} • {hauler.driver}
                        </div>
                      </motion.div>
                    ))}
                  </>
                )}
              </div>

              {/* Selected Unit Detail Panel */}
              {selectedHauler && haulers.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`absolute bottom-4 right-4 w-64 rounded-lg shadow-xl border p-4 ${isDarkMode
                      ? 'bg-slate-800 border-slate-700'
                      : 'bg-white border-gray-200'
                    }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {haulers.find(h => h.id === selectedHauler)?.name}
                    </h4>
                    <button
                      onClick={() => setSelectedHauler(null)}
                      className={`text-gray-400 hover:text-gray-600 ${isDarkMode ? 'hover:text-gray-300' : ''}`}
                    >
                      &times;
                    </button>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Driver</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {haulers.find(h => h.id === selectedHauler)?.driver || 'Unassigned'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Status</span>
                      <Badge variant="outline" className="capitalize">
                        {haulers.find(h => h.id === selectedHauler)?.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Destination</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {haulers.find(h => h.id === selectedHauler)?.destination || '-'}
                      </span>
                    </div>
                    <div className={`pt-2 mt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                      <Button className="w-full h-8 text-xs" variant="secondary">
                        <Navigation className="w-3 h-3 mr-1" /> Re-route
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
